import { createContext, useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import Stepper from '../Stepper';
import BPCompanyDetails from './BPCompanyDetails';
import BPAddress from './BPAddress';
import BPContacts from './BPContacts';
import BPCustomsAndCompliance from './BPCustomsAndCompliance';
import BPCabie from './BPCabie';
import BPBillingDetails from './BPBillingDetails';
import BPModal from './BPModal';
import BPWarningModal from './BPWarningModal';
import BPDocuments from './BPDocuments';
import BPParents from './BPParents';
import BPDefaults from './BPDefaults';

import {
  fetchBusinessPartner,
  saveBusinessPartner,
  fetchBusinessPartnerDropdownValues,
  saveBusinessPartnerParents,
  deleteBPContact,
  fetchDutyAdminDropdownValues,
} from './BusinessPartnerController';
import { trimIfString } from '../../utils/stringUtils';

import { BP_COLUMN_NAMES, BP_STEPPER_TABLES, fetchDataForBPMenu, TAB_VALIDATION_MAPPING } from './utils';

//CSS files
import './BusinessPartnerCard.css';
import BPUploadDocumentModal from './BPUploadDocumentModal';
import BPDocumentListModal from './BPDocumentsListModal';
import BPSyncJiraButton from './BPSyncJiraButton';

//creating a context for the business partner page
export const BusinessPartnerContext = createContext();

const BusinessPartnerCard = ({ id, back, setNeeNor, onSaveCallback }) => {
  //States
  const [currentStep, setCurrentStep] = useState('BPcompanydetails'); //set the current step to be the first tab in the stepper
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  //set BPData and localBPData to be an empty object. They are going to manage what we display on the page and what we save to the database.
  const [BPData, setBPData] = useState({});
  const [localBPData, setLocalBPData] = useState({});

  const [modalShow, setModalShow] = useState(false);
  const [dropdownValues, setDropdownValues] = useState({});
  const [errors, setErrors] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [eoriNumber, setEoriNumber] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [tabValidation, setTabValidation] = useState({});
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [onConfirmFunction, setOnConfirmFunction] = useState(null);
  const [showDocumentsListModal, setShowDocumentsListModal] = useState(false);
  const [jiraSyncInfo, setJiraSyncInfo] = useState(null);

  // Type label for header (e.g. "Trader", "Parent") – show in brackets after company name; no brackets when type is unset
  const typeItem = localBPData['Business Partner']?.find((item) => item.subtitle === 'Type');
  const isParent = typeItem?.value != null && typeItem?.value !== '' && typeItem?.value !== '10';
  const bpTypeLabel =
    typeItem?.value != null && typeItem?.value !== '' && dropdownValues['Type']
      ? (dropdownValues['Type'][typeItem.value] ?? '')
      : '';

  //========================================

  useEffect(() => {
    fetchDataForBPMenu(
      currentStep,
      handleFetchBusinessPartnerDropdownValues,
      handleAddData,
      handleFetchAllDutyAdminDropdowns
    );
  }, [currentStep, id]);

  useEffect(() => {
    fetchTabInfo();
  }, [id]);

  useEffect(() => {
    if (onConfirmFunction) {
      setShowWarningModal(true);
    } else {
      setShowWarningModal(false);
    }
  }, [onConfirmFunction]);

  useEffect(() => {
    const countryData = localBPData.Address?.find((item) => item.subtitle === 'Country');
    if (dropdownValues.Country) {
      if (!countryData?.value || !dropdownValues.Country[countryData.value]) {
        setErrors((prev) => ({ ...prev, Country: 'Country is required and must be selected from the list' }));
      } else {
        setErrors((prev) => {
          const { Country, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [localBPData.Address, dropdownValues.Country]);
  //==============================================

  // function to fetch the data for the tabs. This function will retrieve the eori number, company name and the validation for the tabs, which will be used to display or not the red triangle next to the tab name
  const fetchTabInfo = async () => {
    if (id === 0 || id === null) {
      setCompanyName('New Business Partner');
      setEoriNumber('');
      setJiraSyncInfo(null);
      return;
    }

    const returnData = await fetchBusinessPartner('tabs info', id);
    setCompanyName(returnData[0].companyName);
    setEoriNumber(returnData[0].eori);
    formatTabInfo(returnData[0]);
    const syncRequired = returnData[0].jiraSyncRequired;
    setJiraSyncInfo({
      jiraSyncDate: returnData[0].jiraSyncDate ?? null,
      jiraSyncUser: returnData[0].jiraSyncUser ?? null,
      jiraSyncRequired: syncRequired === null || syncRequired === undefined ? null : Boolean(Number(syncRequired)),
    });
  };

  //function to format the tab info. This function will format the data that we get from the fetchTabInfo function and set the state for isInvalid and tabValidation
  const formatTabInfo = (data) => {
    const returnData = {};
    //loop through the data and get just the key/value pairs where key contains the word 'valid'
    Object.keys(data).forEach((key) => {
      if (key.toLowerCase().includes('valid')) {
        //use the mapping to get the correct key for the tab
        returnData[TAB_VALIDATION_MAPPING[key]] = data[key];
      }
    });
    //set the state is isInvalid using returnData.Invalid, convertes to boolean. Also, set tabValidation with returnData
    setIsInvalid(!!returnData.Invalid);
    setTabValidation(returnData);
  };

  //function to fetch the data for the business partner
  const handleAddData = async (title, activeTab = '', isTest = false) => {
    let returnData;
    //check if id is 0, if it is, then we have a new BP
    if (id === 0 || id === null) {
      returnData = await fetchBusinessPartner(title, null, isTest, id);
    }
    //check if title is a key in BPLocalData. If it is, it means we have already fetched the data for this table. If not, we need to fetch the data
    if (localBPData[title]) {
      return;
    } else {
      //fetch the data for the current table
      returnData = await fetchBusinessPartner(title, id, isTest, id);

      //save the data in the returnData state. returnData will contain the data for all the tables in the current step
      setBPData((prevBPData) => {
        return { ...prevBPData, [title]: returnData };
      });
      setLocalBPData((prevBPData) => {
        return { ...prevBPData, [title]: returnData };
      });
    }
  };

  //function to handle the change of the data. This function will update the localBPData state with the new value, and it will use title / subtitle to find the correct item in the array and update the value in the localBPData state
  const handleOnChange = (subtitle, value, title) => {
    const trimmedValue = trimIfString(value);

    if (title === 'Billable Settings') {
      setErrors((prev) => {
        const next = { ...prev };
        if (subtitle === 'Duty Admin Fee') delete next['Duty Admin Fee'];
        if (subtitle === 'Duty Threshold') delete next['Duty Threshold'];
        if (subtitle === 'Duty Charged to') delete next['Duty Charged to'];
        return next;
      });
    }

    setLocalBPData((prevData) => {
      return {
        ...prevData,
        [title]: [
          ...prevData[title].map((item) => {
            if (item.subtitle === subtitle) {
              return {
                ...item,
                value: trimmedValue,
              };
            }
            return item;
          }),
        ],
      };
    });
  };

  const handleBillableSettingsUnitChange = (subtitle, unitValue, title) => {
    setLocalBPData((prevData) => {
      if (!prevData[title]) return prevData;
      return {
        ...prevData,
        [title]: prevData[title].map((item) => (item.subtitle === subtitle ? { ...item, unit: unitValue } : item)),
      };
    });
  };

  //function to fetch the dropdown values. This function will fetch the dropdown values for the dropdowns in the business partner page
  const handleFetchBusinessPartnerDropdownValues = async (dropdown) => {
    const response = await fetchBusinessPartnerDropdownValues(dropdown);
    //format the data to be an object with the statusNumber as the key and the data as the value
    const formattedData = (response || []).reduce((acc, item) => {
      const statusNumber = item.statusNumber ?? item.id;
      acc[statusNumber] = item.data;
      return acc;
    }, {});
    //set the dropdownValues state with the new data
    setDropdownValues((prevData) => ({
      ...prevData,
      [dropdown]: formattedData,
    }));
  };

  // Duty Admin dropdown API: charged=0 for Duty Admin (%/GBP), charged=1 for Duty Charged To (parent only)
  const handleFetchDutyAdminDropdown = async (charged = 0) => {
    if (id == null || id === 0) return;
    try {
      const response = await fetchDutyAdminDropdownValues(id, charged);
      const formattedData = (response || []).reduce((acc, item) => {
        const statusNumber = String(item.statusNumber ?? item.id);
        acc[statusNumber] = item.data;
        return acc;
      }, {});
      if (Object.keys(formattedData).length > 0) {
        const key = charged === 0 ? 'Duty Admin Dropdown' : 'Duty Charged To';
        setDropdownValues((prev) => ({ ...prev, [key]: formattedData }));
      }
    } catch {
      /* dropdown stays empty until API succeeds */
    }
  };

  // Fetch both duty admin dropdowns in one update to avoid React batching overwriting
  const handleFetchAllDutyAdminDropdowns = async () => {
    if (id == null || id === 0) return;
    try {
      const [res0, res1] = await Promise.all([
        fetchDutyAdminDropdownValues(id, 0),
        fetchDutyAdminDropdownValues(id, 1),
      ]);
      const format = (response) =>
        (response || []).reduce((acc, item) => {
          acc[String(item.statusNumber ?? item.id)] = item.data;
          return acc;
        }, {});
      const data0 = format(res0);
      const data1 = format(res1);
      setDropdownValues((prev) => ({
        ...prev,
        ...(Object.keys(data0).length > 0 && { 'Duty Admin Dropdown': data0 }),
        ...(Object.keys(data1).length > 0 && {
          'Duty Charged To': data1,
          'EORI TAN Usage - To be charged in': data1,
        }),
      }));
    } catch {
      /* dropdowns stay empty until API succeeds */
    }
  };

  // Default EORI TAN Usage - To be charged in to GBP when value is null/empty
  useEffect(() => {
    if (currentStep !== 'BPBillingDetails') return;
    const billableSettings = localBPData['Billable Settings'];
    const eoriTanOptions = dropdownValues?.['EORI TAN Usage - To be charged in'];
    if (!billableSettings || !eoriTanOptions) return;
    const eoriTanItem = billableSettings.find((item) => item.subtitle === 'EORI TAN Usage - To be charged in');
    if (!eoriTanItem || (eoriTanItem.value != null && eoriTanItem.value !== '')) return;
    const gbpKey = Object.entries(eoriTanOptions).find(([, v]) => v === 'GBP')?.[0] ?? 'GBP';
    setLocalBPData((prev) => ({
      ...prev,
      'Billable Settings': prev['Billable Settings'].map((item) =>
        item.subtitle === 'EORI TAN Usage - To be charged in' ? { ...item, value: gbpKey } : item
      ),
    }));
    setBPData((prev) => ({
      ...prev,
      'Billable Settings': prev['Billable Settings'].map((item) =>
        item.subtitle === 'EORI TAN Usage - To be charged in' ? { ...item, value: gbpKey } : item
      ),
    }));
  }, [currentStep, localBPData['Billable Settings'], dropdownValues]);

  // Add Duty Charged to row for parent BPs in Billable Settings
  useEffect(() => {
    if (currentStep !== 'BPBillingDetails') return;
    const bpData = localBPData['Business Partner'];
    const billableSettings = localBPData['Billable Settings'];
    if (!bpData || !billableSettings) return;
    const hasDutyChargedTo = billableSettings.some((item) => item.subtitle === 'Duty Charged to');
    if (isParent && !hasDutyChargedTo) {
      const dutyChargedToRow = { subtitle: 'Duty Charged to', value: null, Type: 'dropdown' };
      setBPData((prev) => ({
        ...prev,
        'Billable Settings': [...(prev['Billable Settings'] || []), dutyChargedToRow],
      }));
      setLocalBPData((prev) => ({
        ...prev,
        'Billable Settings': [...(prev['Billable Settings'] || []), dutyChargedToRow],
      }));
      // Duty Charged To options already fetched by handleFetchAllDutyAdminDropdowns on tab load
    }
  }, [currentStep, localBPData['Business Partner'], localBPData['Billable Settings']]);

  //function to discard changes. This means that we set localBPData to be BPData and we change isEditing to false
  const handleDiscardChanges = () => {
    setLocalBPData(BPData);
    setIsEditing(!isEditing);
    setErrors({});
  };

  //function to handle the edit ability. This function will set isEditing to true or false, depending on the current value
  const handleEdit = () => {
    //if isEditing is true, we call handleDiscardChanges, to discard the changes we made and set isEditing to false. If isEditing is false, we just set isEditing to true
    if (isEditing) {
      handleDiscardChanges();
    } else {
      setIsEditing(!isEditing);
    }
  };

  //function to show the modal. This function will set the modalShow state to true
  const handleShowModal = () => {
    setModalShow(true);
  };

  //function to format the data to post. This function will format the data that we have in localBPData to be in the correct format to post to the database
  const formatDataToPost = (data) => {
    const result = {};

    Object.keys(data).forEach((key) => {
      data[key]?.forEach((item) => {
        if (item.Type === 'feeWithUnit') {
          result.dutyAdminFee = item.value != null && item.value !== '' ? trimIfString(item.value) : null;
          result.dutyAdminDropdown = item.unit != null && item.unit !== '' ? trimIfString(item.unit) : null;
        } else {
          const newKey = BP_COLUMN_NAMES[item.subtitle] || item.subtitle;
          const value = trimIfString(item.value);
          result[newKey] = value;
          if (key === 'Billable Settings' && item.subtitle === 'Duty Charged to') {
            result.billableChargedToIn = value;
          }
        }
      });
    });
    return result;
  };

  const updateDataAfterSave = (data) => {
    setLocalBPData({ ...localBPData, ...data });
    setBPData({ ...localBPData, ...data });
  };

  //function to save the business partner. This function will save the business partner data to the database
  const handleSave = async (e, activeStep) => {
    e.preventDefault();

    setIsSaving(true);

    // Special handling for BPParents since it has a different data structure
    if (activeStep === 'BPParents') {
      try {
        await saveBusinessPartnerParents(localBPData['Parents'] || [], id);
        // Update BPData to match localBPData after successful save
        setBPData(localBPData);
        setIsSaving(false);
        setIsEditing(false);
        fetchTabInfo();
        onSaveCallback?.();
        return;
      } catch (error) {
        console.error('Error saving parents:', error);
        setIsSaving(false);
        return;
      }
    }

    if (activeStep === 'BPContacts') {
      const contacts = (localBPData['Contacts'] || []).slice(0, 5);
      while (contacts.length < 5) contacts.push({ id: null, contactName: '', contactEmail: '' });
      const contactsToSave = contacts
        .map((row, idx) => ({ ...row, _gridIndex: idx }))
        .filter((row) => (row.contactName || '').trim() || (row.contactEmail || '').trim());
      try {
        const payload = {
          contacts: contactsToSave.map((row) => ({
            id: row.id != null && row.id !== '' ? row.id : null,
            contactName: (row.contactName || '').trim(),
            contactEmail: (row.contactEmail || '').trim(),
          })),
        };
        const returnData = await saveBusinessPartner(payload, activeStep, id);
        const newObject = {};
        const hasValidationErrors =
          Array.isArray(returnData) &&
          returnData.some((row) => row?.error || row?.emailAddressError || row?.contactNameError);
        if (hasValidationErrors) {
          returnData.forEach((row, responseIndex) => {
            const gridIndex = contactsToSave[responseIndex]?._gridIndex ?? responseIndex;
            if (row?.emailAddressError) {
              newObject[`Contacts_email_${gridIndex}`] = row.emailAddressError;
            }
            if (row?.contactNameError) {
              newObject[`Contacts_contactName_${gridIndex}`] = row.contactNameError;
            }
          });
          setErrors(newObject);
          setTabValidation((prev) => ({ ...prev, BPContacts: false }));
          setIsSaving(false);
          setIsEditing(false);
          const freshContacts = await fetchBusinessPartner('Contacts', id);
          setBPData((prev) => ({ ...prev, Contacts: freshContacts }));
          setLocalBPData((prev) => ({ ...prev, Contacts: freshContacts }));
          return;
        }
        setErrors({});
        const freshContacts = await fetchBusinessPartner('Contacts', id);
        updateDataAfterSave({ Contacts: freshContacts });
        setBPData((prev) => ({ ...prev, Contacts: freshContacts }));
        setLocalBPData((prev) => ({ ...prev, Contacts: freshContacts }));
        fetchTabInfo();
        setIsSaving(false);
        setIsEditing(false);
        onSaveCallback?.();
        return;
      } catch (error) {
        console.error('Error saving contacts:', error);
        setIsSaving(false);
        return;
      }
    }

    //get the tables for the current step
    const currentTables = BP_STEPPER_TABLES[activeStep];
    // initialize an empty data object
    const data = {};
    //iterate through each table in the current step and add the data to the data object, being the key the table name and the value the data for that table
    currentTables.forEach((table) => {
      data[table] = localBPData[table];
    });

    //format the data to be in the correct format to post
    const formattedData = formatDataToPost(data);
    // add the postponedVatAccounting to the data if the active step is BPcompanydetails
    //if activeStep is BPcompanydetails we need to also get and pass the value of postponedVatAccounting, which is in localBPData["Customs Authorisations"]
    if (activeStep === 'BPcompanydetails') {
      //check if localBPData["Customs Authorisations"] exists
      if (localBPData['Customs Authorisations']) {
        //if it exists, we get the value of postponedVatAccounting from localBPData["Customs Authorisations"], which is the first value in the array
        formattedData.postponedVatAccounting = localBPData['Customs Authorisations'][0].value;
      } else {
        //if it doesn't exist, we need to fetch the data for the customs authorisations table and get the value of postponedVatAccounting from there, only if id is valid
        const customsData = id && (await fetchBusinessPartner('Customs Authorisations', id));
        formattedData.postponedVatAccounting = customsData?.[0]?.value;
      }
    }
    if (activeStep === 'BPDefaults') {
      if (formattedData.countryDespatch === '') {
        formattedData.countryDespatch = null;
      }
      if (formattedData.countryDestination === '') {
        formattedData.countryDestination = null;
      }
      if (formattedData.loadType === '') {
        formattedData.loadType = null;
      }
    }

    //save the data to the database
    const returnData = await saveBusinessPartner(formattedData, activeStep, id);

    //if id is 0, that means we just created a new BP, so we get the id from the return data and set it to be the current id.
    if (id === 0 || id === null) {
      setNeeNor(returnData[0].neeNorId);
    }

    //set the newObject variable to be an object with the errors for the current step
    const newObject = Object.keys(BP_COLUMN_NAMES).reduce((acc, key) => {
      const originalKey = BP_COLUMN_NAMES[key] + 'Error';
      if (originalKey in returnData[0] && returnData[0][originalKey] !== null) {
        acc[key] = returnData[0][originalKey];
      }
      return acc;
    }, {});

    // Billing: map API errors to field keys
    if (activeStep === 'BPBillingDetails') {
      if (returnData[0]?.dutyAdminFeeError) {
        newObject['Duty Admin Fee'] = returnData[0].dutyAdminFeeError;
      } else if (returnData[0]?.dutyAdminFeeUnitError) {
        newObject['Duty Admin Fee'] = returnData[0].dutyAdminFeeUnitError;
      }
      if (returnData[0]?.dutyThresholdError) {
        newObject['Duty Threshold'] = returnData[0].dutyThresholdError;
      }
      if (returnData[0]?.dutyChargedToError) {
        newObject['Duty Charged to'] = returnData[0].dutyChargedToError;
      }
    }

    // Billing: if validation errors, set tab warning and exit edit mode (same as other tabs)
    if (activeStep === 'BPBillingDetails' && Object.keys(newObject).length > 0) {
      setErrors(newObject);
      setTabValidation((prev) => ({ ...prev, BPBillingDetails: false }));
      setIsSaving(false);
      setIsEditing(false);
      // Re-fetch billing data so saved values display correctly
      const [companyPaymentData, billableSettingsData] = await Promise.all([
        fetchBusinessPartner('Company Payment Details', id, false, id),
        fetchBusinessPartner('Billable Settings', id, false, id),
      ]);
      setBPData((prev) => ({
        ...prev,
        'Company Payment Details': companyPaymentData,
        'Billable Settings': billableSettingsData,
      }));
      setLocalBPData((prev) => ({
        ...prev,
        'Company Payment Details': companyPaymentData,
        'Billable Settings': billableSettingsData,
      }));
      return;
    }

    //call the updateDataAfterSave function - this is used if we need another page to be refreshed
    updateDataAfterSave(data);

    //set the errors state with the newObject, fetch the tab info (to update the company name and eori number), set bpData to be localBPData (since we saved the new info successfully) and set isSaving and isEditing to false
    setErrors(newObject);
    fetchTabInfo();
    setIsSaving(false);
    setIsEditing(false);
    onSaveCallback?.();
  };

  //function to close the modal. This function will set the modalShow state to false
  const handleCLoseModal = () => {
    setModalShow(false);
  };

  const hasContactValidationErrors = Object.keys(errors || {}).some(
    (k) => k.startsWith('Contacts_email_') || k.startsWith('Contacts_contactName_')
  );

  //function to show the warning modal. This function will set the showWarningModal state to true and also take care of the onConfirm callback function
  const handleShowWarningModal = (onConfirm) => {
    const isSame = JSON.stringify(BPData) === JSON.stringify(localBPData);
    const hasValidationErrors = currentStep === 'BPContacts' && hasContactValidationErrors;
    const hasBillingErrors = currentStep === 'BPBillingDetails' && Object.keys(errors || {}).length > 0;

    if (isSame || hasValidationErrors || hasBillingErrors) {
      onConfirm();
      if (!isSame) handleDiscardChanges();
    } else {
      const onConfirmFunction = () => {
        onConfirm();
        handleDiscardChanges();
      };
      setOnConfirmFunction(() => onConfirmFunction);
    }
  };

  // Contacts: update one field for one row
  const handleContactsChange = (index, field, value) => {
    setLocalBPData((prev) => {
      const contacts = prev['Contacts'] ? [...prev['Contacts']] : [];
      const padded = contacts.slice(0, 5);
      while (padded.length < 5) padded.push({ id: null, contactName: '', contactEmail: '' });
      if (padded[index]) padded[index] = { ...padded[index], [field]: value };
      return { ...prev, Contacts: padded };
    });
    if (field === 'contactEmail') {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`Contacts_email_${index}`];
        return next;
      });
    }
    if (field === 'contactName') {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`Contacts_contactName_${index}`];
        return next;
      });
    }
  };

  // Contacts: clear row at index and move rows below up (empty at end); if row has id, call DELETE API first
  const emptyContactRow = () => ({ id: null, contactName: '', contactEmail: '' });
  const handleContactDelete = async (index) => {
    const contacts = (localBPData['Contacts'] || []).slice(0, 5);
    const row = contacts[index];
    if (row?.id != null) {
      try {
        await deleteBPContact(row.id, id);
        fetchTabInfo();
      } catch (err) {
        console.error('Error deleting contact:', err);
        return;
      }
    }
    setLocalBPData((prev) => {
      const list = prev['Contacts'] ? [...prev['Contacts']] : [];
      const next = list.slice(0, 5);
      next[index] = emptyContactRow();
      const nonEmpty = next.filter((r) => (r.contactName || '').trim() || (r.contactEmail || '').trim());
      const emptyCount = 5 - nonEmpty.length;
      const reordered = [...nonEmpty];
      for (let i = 0; i < emptyCount; i++) reordered.push(emptyContactRow());
      return { ...prev, Contacts: reordered };
    });
    setErrors((prev) => {
      const next = { ...prev };
      [0, 1, 2, 3, 4].forEach((i) => {
        delete next[`Contacts_email_${i}`];
        delete next[`Contacts_contactName_${i}`];
      });
      return next;
    });
  };

  //function to handle checkbox changes in BPParents. This function will update the checked value for a specific parent in localBPData
  const handleParentsCheckboxChange = (parentTraderId, checked) => {
    if (!isEditing) return;

    // Update the checked value for the specific parent in localBPData
    setLocalBPData((prevData) => {
      const updatedParents = (prevData['Parents'] || []).map((parent) => {
        if (parent.ParentTraderId === parentTraderId) {
          return {
            ...parent,
            checked: checked,
          };
        }
        return parent;
      });

      return {
        ...prevData,
        Parents: updatedParents,
      };
    });
  };

  //provider values
  const providerValues = {
    isEditing,
    handleEdit,
    handleAddData,
    BPData,
    handleOnChange,
    handleBillableSettingsUnitChange,
    localBPData,
    isSaving,
    handleSave,
    modalShow,
    handleShowModal,
    handleFetchBusinessPartnerDropdownValues,
    handleFetchDutyAdminDropdown,
    dropdownValues,
    errors,
    setErrors,
    back,
    companyName,
    eoriNumber,
    businessPartnerId: id,
    jiraSyncInfo,
    fetchTabInfo,
    bpTypeLabel,
    isTraderBP: typeItem?.value === '10',
    isParentBP: typeItem?.value != null && typeItem?.value !== '' && typeItem?.value !== '10',
    isInvalid,
    setCurrentStep,
    handleShowWarningModal,
    setShowUploadDocumentModal,
    setShowDocumentsListModal,
    handleParentsCheckboxChange,
    handleContactsChange,
    handleContactDelete,
    hasContactValidationErrors,
  };

  // Function to handle step changes
  const handleStepChange = (stepkey) => {
    if (stepkey !== currentStep) {
      setCurrentStep(stepkey);
    }
  };

  const handleChangeNotesData = (data) => {
    //set BPData and localBPData with the new data for Trader Notes
    setBPData((prevData) => {
      return {
        ...prevData,
        ['Trader Notes']: [
          {
            ...prevData['Trader Notes'][0],
            data: data,
            value: data?.length,
          },
        ],
      };
    });

    setLocalBPData((prevData) => {
      return {
        ...prevData,
        ['Trader Notes']: [
          {
            ...prevData['Trader Notes'][0],
            data: data,
            value: data?.length,
          },
        ],
      };
    });
  };

  // conditionally render BPParents step only if Business Partner Type is '1' - 'Trader'
  const shouldShowBPParents = () => {
    const businessPartnerData = BPData['Business Partner'];
    if (!businessPartnerData) return false;

    const typeItem = businessPartnerData.find((item) => item.subtitle === 'Type');
    return typeItem?.value === '10';
  };

  return (
    <BusinessPartnerContext.Provider value={providerValues}>
      <BPModal
        show={modalShow}
        onHide={handleCLoseModal}
        onChange={handleOnChange}
        customerId={id}
        notesData={BPData?.['Trader Notes']?.[0]?.data ?? []}
        handleChangeNotesData={handleChangeNotesData}
      />
      <BPDocumentListModal
        companyId={id}
        show={showDocumentsListModal}
        onHide={() => setShowDocumentsListModal(false)}
      />
      <BPWarningModal show={showWarningModal} onHide={() => setOnConfirmFunction(null)} onConfirm={onConfirmFunction} />
      <BPUploadDocumentModal
        show={showUploadDocumentModal}
        onHide={() => setShowUploadDocumentModal(false)}
        customerId={id}
        currentStep={currentStep}
      />
      <Container className="container-md">
        <section
          className="floating-box"
          style={{ maxWidth: '1000px', backgroundColor: 'white', marginLeft: 'auto', marginRight: 'auto' }}
        >
          <Stepper
            showFuture={true}
            canSkipForward={true}
            stepClassName="business-partner-section"
            tabValidation={tabValidation}
            useModalToChangeTabs={true}
            handleShowWarningModal={handleShowWarningModal}
            onActiveStepChange={handleStepChange} // Pass the step change handler
          >
            <main className="business-partner-container no-graphics" style={{ position: 'relative' }}>
              <div className="bp-sidebar-sync">
                <BPSyncJiraButton
                  businessPartnerId={id}
                  localBPData={localBPData}
                  errors={errors}
                  jiraSyncInfo={jiraSyncInfo}
                  onSyncSuccess={fetchTabInfo}
                />
              </div>
              <div className="bp-sidebar">
                <Stepper.StepList
                  isSticky={true}
                  title={<div className="epo-steps-stepper-header">Business Partner</div>}
                />
              </div>
              <Stepper.Step text="Company Details" stepKey="BPcompanydetails">
                <BPCompanyDetails />
              </Stepper.Step>
              {id === 0 || id == null ? null : ( //only display this block is id is not null or 0
                <>
                  <Stepper.Step text="Address" stepKey="BPaddress">
                    <BPAddress />
                  </Stepper.Step>
                  <Stepper.Step text="Contacts" stepKey="BPContacts">
                    <BPContacts />
                  </Stepper.Step>
                  <Stepper.Step text="Customs & Compliance" stepKey="BPCustomsAndCompliance">
                    <BPCustomsAndCompliance />
                  </Stepper.Step>
                  <Stepper.Step text="CABIE" stepKey="BPCabie">
                    <BPCabie />
                  </Stepper.Step>
                  {!shouldShowBPParents() && (
                    <Stepper.Step text="Defaults" stepKey="BPDefaults" hideFromStepList={false}>
                      <BPDefaults id={id} />
                    </Stepper.Step>
                  )}
                  <Stepper.Step text="Billing Details" stepKey="BPBillingDetails">
                    <BPBillingDetails />
                  </Stepper.Step>
                  <Stepper.Step text="Documents" stepKey="BPDocuments">
                    <BPDocuments companyId={id} isModalShowing={showUploadDocumentModal} />
                  </Stepper.Step>
                  {shouldShowBPParents() && (
                    <Stepper.Step text="Parents" stepKey="BPParents">
                      <BPParents />
                    </Stepper.Step>
                  )}
                </>
              )}
            </main>
          </Stepper>
        </section>
      </Container>
    </BusinessPartnerContext.Provider>
  );
};
export default BusinessPartnerCard;
